"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var React = require("react");
var TypeStyle = require("typestyle");
var FocusIndicators_1 = require("../../styles/FocusIndicators");
var TabStyles_1 = require("../../styles/TabStyles");
var BrowserDetection_1 = require("../../utils/BrowserDetection");
var Declarative_1 = require("../../utils/Declarative");
var DomUtil_1 = require("../../utils/DomUtil");
var ReactHelpers_1 = require("../../utils/ReactHelpers");
var Combobox_1 = require("./Combobox");
var RowHeight = TabStyles_1.TabStyles.Sizing.RowHeight;
var FontSize = TabStyles_1.TabStyles.Typography.Sizing.Default;
var BorderWidth = 1;
var PickerPadding = 3;
var ChipMargin = 4;
var LabelStyleName = TypeStyle.style({
    color: '#4F4F4F',
    display: 'inline-block',
    fontSize: FontSize,
    marginBottom: 4,
});
var ChipPickerStyleName = TypeStyle.style({
    alignContent: 'flex-start',
    alignItems: 'flex-start',
    border: BorderWidth + "px solid #cbcbcb",
    borderRadius: 1,
    boxSizing: 'border-box',
    cursor: 'text',
    display: 'flex',
    flexWrap: 'wrap',
    fontSize: FontSize,
    lineHeight: RowHeight - 2 + "px",
    overflowY: 'auto',
    padding: PickerPadding,
    paddingRight: 18,
    position: 'relative',
    $nest: {
        '&:hover': { backgroundColor: '#fafafa', borderColor: '#333' },
        '&.focus-within': tslib_1.__assign({}, FocusIndicators_1.DefaultFocusIndicator, { backgroundColor: 'transparent', $nest: { '&[aria-invalid="true"]': { boxShadow: "0 0 4px 0 #E62835" } } }),
        '&[aria-invalid="true"]': { borderColor: '#c41723' },
        '& > *': { margin: ChipMargin },
        '& > input': {
            fontSize: 'inherit',
            lineHeight: FontSize + "px",
            marginLeft: 0,
            marginRight: 0,
            outline: 'none',
        },
        '& > input:focus': BrowserDetection_1.isEdge ? { marginLeft: -1, marginRight: -3 } : { marginRight: -2 },
        '& > input:focus:first-child': { marginLeft: 4 },
        '& > .drag-indicator': {
            background: '#EB8F50',
            height: RowHeight + (2 * ChipMargin),
            margin: "0 -1px",
            pointerEvents: 'none',
            width: 2,
        }
    }
});
/* Chip multi-selection disabled until after SP1
type ListContentCache = { [key: string]: React.ReactNode };

export function buildContentCache(listItems: ChipPickerProps['listItems']): ListContentCache {
  return listItems.reduce((cache: ListContentCache, value) => (cache[value.value] = value.content || value.value, cache), {});
}

export function buildSelectionCache(selectedValues: string[]): ChipPickerState['selectedChips'] {
  return selectedValues.reduce((cache: ChipPickerState['selectedChips'], value) => (cache[value] = true, cache), {});
}

export function reduceSelectionCache(cache: ChipPickerState['selectedChips']): string[] {
  return Object.keys(cache).filter(key => cache[key] === true);
}
*/
// function cloneAndInsert<T>(values: Readonly<T[]>, value: T, index: number): T[] {
//   const newValues = [...values];
//   newValues.splice(index, 0, value);
//   return newValues;
// }
var getPixelHeight = function (lines) {
    return ((BorderWidth + PickerPadding) * 2 + (ChipMargin * 2 + RowHeight) * Math.max(1, lines));
};
var ChipPicker = /** @class */ (function (_super) {
    tslib_1.__extends(ChipPicker, _super);
    function ChipPicker() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.domRef = React.createRef();
        _this.inputElement = null;
        _this.inputId = DomUtil_1.generateId();
        _this.labelId = DomUtil_1.generateId();
        _this.findIndexOfChipValue = function (value) { return _this.props.children.findIndex(function (chip) { return chip.props.value === value; }); };
        _this.getChipElemAtIndex = function (chipIndex) {
            return _this.domRef.current.querySelector("[role=\"option\"][aria-posinset=\"" + (chipIndex + 1) + "\"]");
        };
        _this.getChipValue = function (index) { return (_this.props.children[index] || { props: { value: undefined } }).props.value; };
        _this.getChipValues = function () { return _this.props.children.map(function (child) { return child.props.value; }); };
        _this.getLastChipValue = function () { return _this.getChipValue(_this.props.children.length - 1); };
        _this.state = {
            activeChipValue: _this.getLastChipValue(),
            cursorOffset: 0,
            dragIndex: -1,
            focusWithin: false,
            selectedChips: {}
        };
        /** used for positioning the list popup */
        _this.getAnchorClientRect = function () { return _this.domRef.current.getBoundingClientRect(); };
        /* Chip multi-selection disabled until after SP1
        // private selectAllChips = (): void => this.setState({ selectedChips: buildSelectionCache(this.getChipValues()) });
        // private clearSelectedChips = () => this.setState({ selectedChips: {} });
        */
        _this.chipKeyDownHandlers = Object.freeze({
            'ArrowLeft': function () { return _this.setState({ cursorOffset: 0 }, _this.focusInput); },
            'ArrowRight': function () { return _this.setState({ cursorOffset: 1 }, _this.focusInput); },
            'Home': function () { return _this.setState({ activeChipValue: _this.getChipValue(0), cursorOffset: 0 }, _this.focusInput); },
            'End': function () { return _this.setState({ activeChipValue: _this.getLastChipValue(), cursorOffset: 1 }, _this.focusInput); },
            'Backspace': function (currentIndex) { return _this.removeValueAtIndex(currentIndex); },
            'Delete': function (currentIndex) { return _this.removeValueAtIndex(currentIndex); },
            'Ctrl+a': function () { return; },
        });
        _this.inputKeyDownHandlers = Object.freeze({
            'ArrowLeft': function (focusables, currentIndex) {
                var nextIndex = Math.max(currentIndex - 1, 0);
                _this.setState({ cursorOffset: currentIndex > 0 ? 1 : 0 }, function () { return focusables[nextIndex].focus(); });
            },
            'ArrowRight': function (focusables, currentIndex) {
                var nextIndex = Math.min(currentIndex + 1, focusables.length - 1);
                _this.setState({ cursorOffset: currentIndex < focusables.length - 1 ? 0 : 1 }, function () { return focusables[nextIndex].focus(); });
            },
            'Home': function () { return _this.setState({ activeChipValue: _this.getChipValue(0), cursorOffset: 0 }, _this.focusInput); },
            'End': function () { return _this.setState({ activeChipValue: _this.getLastChipValue(), cursorOffset: 1 }, _this.focusInput); },
            'Backspace': function (focusables, currentIndex) { return _this.inputKeyDownHandlers.ArrowLeft(focusables, currentIndex); },
            'Delete': function (focusables, currentIndex) { return _this.inputKeyDownHandlers.ArrowRight(focusables, currentIndex); },
        });
        _this.handleKeyDown = ReactHelpers_1.mergeEventHandlers(function (e) {
            var targetIsChip = function (target) { return target.getAttribute('role') === 'option'; };
            var targetIsInput = function (target) { return target.tagName === 'INPUT'; };
            var key = (BrowserDetection_1.isCtrl(e) ? 'Ctrl+' : '') + e.key;
            if (targetIsChip(e.target) && _this.chipKeyDownHandlers[key]) {
                _this.chipKeyDownHandlers[key](_this.findIndexOfChipValue(_this.state.activeChipValue));
                e.preventDefault();
                e.stopPropagation();
            }
            else if (targetIsInput(e.target) && e.target.value === '' && _this.inputKeyDownHandlers[key]) {
                var focusables = Array.from(e.currentTarget.querySelectorAll('[role="option"], input'));
                var currentIndex = focusables.indexOf(e.target);
                _this.inputKeyDownHandlers[key](focusables, currentIndex);
                e.preventDefault();
                e.stopPropagation();
            }
        }, function (e) { return ReactHelpers_1.safeInvoke(_this.props.onKeyDown, e); });
        _this.getDragIndexFromDragEvent = function (e) {
            var dragOnContainer = e.target === e.currentTarget;
            var inLeftGutter = dragOnContainer && e.nativeEvent.offsetX < (BorderWidth + PickerPadding + ChipMargin);
            var nearbyChip = dragOnContainer ?
                _this.findNearbyChipFromMouseEvent(e, !inLeftGutter) :
                DomUtil_1.closest(e.target, "[role=\"option\"]");
            return nearbyChip ? parseInt(nearbyChip.getAttribute('aria-posinset'), 10) - (inLeftGutter ? 1 : 0) : _this.getChipValues().length;
        };
        _this.handleDragOver = ReactHelpers_1.mergeEventHandlers(function (e) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            var dragIndex = _this.getDragIndexFromDragEvent(e);
            if (dragIndex >= 0 && dragIndex <= _this.getChipValues().length) {
                _this.setState({ dragIndex: dragIndex });
            }
        }, function (e) { return ReactHelpers_1.safeInvoke(_this.props.onDragOver, e); });
        _this.handleDragExit = ReactHelpers_1.mergeEventHandlers(function (e) {
            _this.setState({ dragIndex: -1 });
        }, function (e) { return ReactHelpers_1.safeInvoke(_this.props.onDragExit, e); });
        _this.handleDrop = ReactHelpers_1.mergeEventHandlers(function (e) {
            var to = _this.state.dragIndex;
            var data = e.dataTransfer.getData('Text');
            _this.setState({ dragIndex: -1 });
            if (!_this.props.onValuesChange || !data) {
                return;
            }
            var from = _this.findIndexOfChipValue(data);
            var values = _this.getChipValues().slice();
            if (!isFinite(to) || from >= values.length || to > values.length) {
                return;
            }
            e.preventDefault();
            if (from < 0 && _this.props.onValueAdd && _this.props.listItems.findIndex(function (item) { return item.value === data; })) {
                _this.props.onValueAdd(data, to);
            }
            else {
                values.splice(to - (from >= to ? 0 : 1), 0, values.splice(from, 1)[0]);
                _this.props.onValuesChange(values);
            }
        }, function (e) { return ReactHelpers_1.safeInvoke(_this.props.onDrop, e); });
        /** pseudo-simulate a `<textarea>` by allowing mouse clicks to move the "cursor" around (i.e. between chips) */
        _this.handleMouseDown = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (e.currentTarget !== e.target) {
                return;
            }
            var inLeftGutter = e.nativeEvent.offsetX < (BorderWidth + PickerPadding + ChipMargin);
            var nearbyChip = _this.findNearbyChipFromMouseEvent(e, !inLeftGutter);
            if (nearbyChip) {
                nearbyChip.focus();
                _this.setState({ cursorOffset: inLeftGutter ? 0 : 1 }, _this.focusInput);
            }
            else {
                _this.setState({ activeChipValue: _this.getLastChipValue(), cursorOffset: 1 });
            }
        }, function (e) { return ReactHelpers_1.safeInvoke(_this.props.onMouseDown, e); });
        _this.handleBlur = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (!e.currentTarget.contains(DomUtil_1.findBlurRelatedTarget(e))) {
                _this.setState({ focusWithin: false });
            }
        }, function (e) { return ReactHelpers_1.safeInvoke(_this.props.onBlur, e); });
        _this.handleFocus = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (e.currentTarget === e.target) {
                _this.focusInput();
            }
            if (!_this.state.focusWithin) {
                _this.setState({ focusWithin: true });
            }
        }, function (e) { return ReactHelpers_1.safeInvoke(_this.props.onFocus, e); });
        _this.handleInputRef = function (ref) { return _this.inputElement = ref; };
        _this.handleSelectValue = function (value) {
            if (_this.props.onValueAdd) {
                var insertionIndex = Math.max(0, _this.findIndexOfChipValue(_this.state.activeChipValue) + _this.state.cursorOffset);
                _this.props.onValueAdd(value, insertionIndex);
            }
        };
        _this.removeValueAtIndex = function (index) {
            var onValuesChange = _this.props.onValuesChange;
            var newValues = _this.getChipValues();
            newValues.splice(index, 1);
            if (onValuesChange) {
                onValuesChange(newValues);
            }
        };
        return _this;
    }
    ChipPicker.prototype.componentDidUpdate = function (prevProps, prevState) {
        var children = this.props.children;
        var prevChildren = prevProps.children;
        if (children.length < prevChildren.length) {
            // when a chip is removed, put the cursor in its prior position
            var removedIndex = prevChildren.findIndex(function (chip) { return chip.props.value === prevState.activeChipValue; });
            var activeChipValue = this.getChipValue(removedIndex) || this.getLastChipValue();
            this.setState({ activeChipValue: activeChipValue, cursorOffset: removedIndex === children.length ? 1 : 0 }, this.focusInput);
        }
        else if (children.length === prevChildren.length + 1) {
            // when a chip is added, put the cursor after it
            var activeChipIndex = 0;
            while (activeChipIndex < children.length - 1 && this.getChipValue(activeChipIndex) === prevChildren[activeChipIndex].props.value) {
                activeChipIndex++;
            }
            this.setState({ activeChipValue: this.getChipValue(activeChipIndex), cursorOffset: 1 });
        }
        // make sure the most recent change is visible, unless actively dragging
        if (this.state.dragIndex < 0) {
            if (children.length !== prevChildren.length) {
                DomUtil_1.scrollIntoView(this.inputElement);
                if (this.state.focusWithin) {
                    this.focusInput();
                }
            }
            else {
                var chipIndex = this.findIndexOfChipValue(this.state.activeChipValue);
                DomUtil_1.scrollIntoView(this.getChipElemAtIndex(chipIndex));
            }
        }
    };
    ChipPicker.prototype.focusChipAtIndex = function (chipIndex) {
        var chipElem = this.getChipElemAtIndex(chipIndex);
        if (chipElem) {
            chipElem.focus();
        }
    };
    ChipPicker.prototype.focusInput = function () {
        if (this.inputElement) {
            this.inputElement.focus();
        }
    };
    ChipPicker.prototype.findNearbyChipFromMouseEvent = function (e, searchBackwards) {
        if (searchBackwards === void 0) { searchBackwards = true; }
        if (e.currentTarget.querySelector('[role="option"]') === null) {
            return null;
        }
        var node = e.currentTarget;
        var clientX = e.clientX, clientY = e.clientY;
        while (node === e.currentTarget || node.tagName === 'INPUT') {
            searchBackwards ? --clientX : ++clientX;
            node = document.elementFromPoint(clientX, clientY);
        }
        return DomUtil_1.closest(node, "[role=\"option\"]");
    };
    ChipPicker.prototype.renderChips = function () {
        var _this = this;
        var chipCount = React.Children.count(this.props.children);
        return React.Children.map(this.props.children, function (child, index) {
            var value = child.props.value;
            return React.cloneElement(child, tslib_1.__assign({ 'aria-atomic': true, 'aria-posinset': index + 1, 'aria-selected': _this.state.selectedChips[value], 'aria-setsize': chipCount, draggable: true, key: value }, (BrowserDetection_1.isIE && { onMouseDown: function () { return setTimeout(function () { return _this.focusChipAtIndex(index); }); } }), { 
                /* Chip multi-selection disabled until after SP1
                // onClick: () => this.setState(prevState => ({
                  selectedChips: { ...prevState.selectedChips, [value]: !prevState.selectedChips[value] }
                })),
                */
                onFocus: function () { return _this.setState({ activeChipValue: value }); }, onDragStart: function (e) {
                    e.dataTransfer.setData('Text', value);
                    e.dataTransfer.effectAllowed = 'move';
                }, onRemove: function () { return _this.removeValueAtIndex(index); }, role: 'option', tabIndex: -1 }));
        });
    };
    ChipPicker.prototype.renderCombobox = function (props) {
        return React.createElement(Combobox_1.Combobox, tslib_1.__assign({}, props, { getAnchorClientRect: this.getAnchorClientRect, id: this.inputId, inputRef: this.handleInputRef, key: 'combobox', listItems: props.listItems, onSelectValue: this.handleSelectValue, testId: Declarative_1.testId(props.testId, 'ChipPicker') }));
    };
    ChipPicker.prototype.render = function () {
        // tslint:disable:max-line-length
        var _a = this.props, children = _a.children, className = _a.className, onFilterItems = _a.onFilterItems, label = _a.label, maxLines = _a.maxLines, listItems = _a.listItems, listMaxHeight = _a.listMaxHeight, onMouseDown = _a.onMouseDown, onValueAdd = _a.onValueAdd, onValuesChange = _a.onValuesChange, placeholder = _a.placeholder, stackingRootId = _a.stackingRootId, style = _a.style, testId = _a.testId, htmlProps = tslib_1.__rest(_a, ["children", "className", "onFilterItems", "label", "maxLines", "listItems", "listMaxHeight", "onMouseDown", "onValueAdd", "onValuesChange", "placeholder", "stackingRootId", "style", "testId"]);
        var _b = this.state, activeChipValue = _b.activeChipValue, cursorOffset = _b.cursorOffset, dragIndex = _b.dragIndex;
        var chips = this.renderChips();
        if (dragIndex < 0) {
            // insert the combobox between chips so the whole control feels like a big text field with discrete chips
            var activeChipIndex = this.findIndexOfChipValue(activeChipValue);
            var placeholderText = chips.length > 0 ? undefined : placeholder;
            var combobox = this.renderCombobox({ onFilterItems: onFilterItems, listItems: listItems, listMaxHeight: listMaxHeight, placeholder: placeholderText, stackingRootId: stackingRootId, testId: testId });
            var comboIndex = activeChipIndex + cursorOffset;
            chips.splice(comboIndex, 0, combobox);
        }
        else {
            // insert the drag indicator at the current dragIndex
            var dragIndicator = React.createElement("div", { className: 'drag-indicator', key: 'drag-indicator' });
            chips.splice(dragIndex, 0, dragIndicator);
        }
        // tslint:enable:max-line-length
        return React.createElement(React.Fragment, null,
            React.createElement("label", { children: label, className: LabelStyleName, "data-tb-test-id": Declarative_1.testId(testId, 'ChipPicker', 'Label'), htmlFor: this.inputId, id: this.labelId }),
            React.createElement("div", tslib_1.__assign({}, ReactHelpers_1.EventUtilsOptOut, { "aria-labelledby": this.labelId, "aria-orientation": 'horizontal', "data-tb-test-id": Declarative_1.testId(testId, 'ChipPicker'), spellCheck: false }, htmlProps, { children: chips, className: TypeStyle.classes(ChipPickerStyleName, className, this.state.focusWithin && 'focus-within'), onBlur: this.handleBlur, onDragExit: this.handleDragExit, onDragOver: this.handleDragOver, onDrop: this.handleDrop, onFocus: this.handleFocus, onKeyDown: this.handleKeyDown, onMouseDown: this.handleMouseDown, ref: this.domRef, role: 'listbox', style: tslib_1.__assign({ maxHeight: getPixelHeight(maxLines) }, style), tabIndex: -1 })));
    };
    ChipPicker.defaultProps = {
        children: [],
        maxLines: 3,
    };
    return ChipPicker;
}(React.PureComponent));
exports.ChipPicker = ChipPicker;
//# sourceMappingURL=ChipPicker.js.map